/**
 * ShareBubbleRow — the main row of a share bubble: the 40×40
 * `ShareIconSquare` next to the shared payload, ordered by ownership. Shared
 * by BOTH share parents (`ShareContentBubble` and `ShareUserBubble`), which
 * rendered an identical row (same gap, same ordering rule) before this
 * extraction. The parents own the outer column and the optional caption
 * beneath it; this component owns only the row.
 *
 * Row order — the icon always sits on the OUTER edge of the bubble:
 *  - Share-user: own renders the share-icon-square first, to the LEFT of the
 *    peer-info-bubble (`chat_screen.dart:1947-2037`); peer renders it last, to
 *    the RIGHT of the peer-info-bubble (`:3074-3164`).
 *  - Share-content: own renders `[share-icon-square][media-thumbnail]`
 *    (`chat_screen.dart:2139-2238`); peer renders
 *    `[media-thumbnail][share-icon-square]` (`chat_screen.dart:3267-3366`).
 *
 * Responsive tokens (closest available on the project scale):
 *  - Share-icon ↔ payload gap 8 → `responsive.spacing.sm` (= 8). Exact Flutter
 *    parity (`EdgeInsets.only(right: 8)` on the icon for own /
 *    `EdgeInsets.only(left: 8)` for peer).
 *
 * No QA handle of its own — each parent's wrapper handle already spans the row.
 */

import React from 'react';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ShareIconSquare } from './ShareIconSquare';

interface ShareBubbleRowProps {
  /** Whether the message is owned by the current user (drives the row order). */
  isOwn: boolean;
  /**
   * The shared payload rendered beside the icon — the peer-info block for
   * share-user, the media thumbnail for share-content.
   */
  children: React.ReactNode;
}

export const ShareBubbleRow: React.FC<ShareBubbleRowProps> = ({
  isOwn,
  children,
}) => {
  const responsive = useResponsiveDesign();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: responsive.spacing.sm,
        maxWidth: '100%',
      }}
    >
      {isOwn ? (
        <>
          <ShareIconSquare />
          {children}
        </>
      ) : (
        <>
          {children}
          <ShareIconSquare />
        </>
      )}
    </div>
  );
};
